import Phaser from 'phaser';
import { BaseScene } from '@core/scenes/BaseScene';
import { SceneKeys, GAME_TITLE, Palette } from '@core/config/constants';
import { GROUP_NAME, findCharacter } from '@core/config/characters';
import { findShopItem } from '@core/config/shop';
import { TextButton } from '@ui/components/TextButton';
import { MinigameRegistry } from '@core/registry/MinigameRegistry';
import type { MinigameDefinition } from '@core/types';

const MAX_CARD_WIDTH = 300;

/**
 * Menu principal del juego. Lee el catalogo de minijuegos del registry y dibuja una
 * tarjeta por cada uno, reacomodando la grilla segun el tamaño de pantalla. Tocar una
 * tarjeta inicia la escena del minijuego.
 */
export class HubScene extends BaseScene {
  public constructor() {
    super(SceneKeys.Hub);
  }

  public create(): void {
    this.initResponsive();
    this.addSelectedBackground();
    this.addTwinkles(24);

    const { height } = this.scale;
    const titleY = height * 0.07;

    const title = this.add
      .text(this.centerX, titleY, GAME_TITLE, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '58px',
        color: Palette.textLight,
      })
      .setOrigin(0.5);

    this.tweens.add({
      targets: title,
      scale: 1.05,
      duration: 1400,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this.add
      .text(this.centerX, titleY + 44, `con ${GROUP_NAME}`, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '24px',
        color: '#4dd0e1',
      })
      .setOrigin(0.5);

    const navY = titleY + 96;
    const navBottom = this.renderNav(navY);

    this.renderCoins();
    this.renderSoundToggle();
    this.renderMinigameCards(MinigameRegistry.list(), navBottom + 24);
    this.renderSelectedCharacter();
  }

  /** Dibuja la barra de navegacion (se acomoda en una o dos filas) y devuelve su borde inferior. */
  private renderNav(navY: number): number {
    const { width } = this.scale;
    const gap = Math.max(16, width * 0.02);
    const buttons = [
      { label: '★ Personajes', color: Palette.secondary, target: SceneKeys.CharacterSelect },
      { label: '🛍️ Tienda', color: Palette.primary, target: SceneKeys.Store },
      { label: '🎬 Escenarios', color: 0x9b59b6, target: SceneKeys.Scenarios },
      { label: '🏆 Logros', color: Palette.accent, target: SceneKeys.Achievements },
      { label: '🎯 Misiones', color: 0x2ecc71, target: SceneKeys.Missions },
    ];

    const buttonHeight = 56;
    const rowGap = 14;
    const perRow = width >= 1024 ? buttons.length : 2;
    const rows = Math.ceil(buttons.length / perRow);
    const buttonWidth = Math.min(240, (width * 0.94 - (perRow - 1) * gap) / perRow);

    buttons.forEach((button, index) => {
      const row = Math.floor(index / perRow);
      const column = index % perRow;
      const itemsInRow = Math.min(perRow, buttons.length - row * perRow);
      const rowWidth = itemsInRow * buttonWidth + (itemsInRow - 1) * gap;
      const startX = this.centerX - rowWidth / 2 + buttonWidth / 2;

      new TextButton(this, {
        x: startX + column * (buttonWidth + gap),
        y: navY + row * (buttonHeight + rowGap),
        label: button.label,
        width: buttonWidth,
        height: buttonHeight,
        color: button.color,
        onClick: () => this.scene.start(button.target),
      });
    });

    return navY + (rows - 1) * (buttonHeight + rowGap) + buttonHeight / 2;
  }

  private renderSoundToggle(): void {
    const button = this.add
      .text(28, 22, this.audio.isMuted ? '🔇' : '🔊', { fontSize: '34px' })
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true });

    button.on(Phaser.Input.Events.POINTER_UP, () => {
      const muted = this.audio.toggleMute();
      button.setText(muted ? '🔇' : '🔊');
      if (!muted) {
        this.audio.playClick();
      }
    });
  }

  private renderCoins(): void {
    this.add
      .text(this.scale.width - 28, 24, `⭐ ${this.profile.coins}`, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '30px',
        color: Palette.textLight,
      })
      .setOrigin(1, 0);
  }

  private renderSelectedCharacter(): void {
    const { width, height } = this.scale;
    const character = findCharacter(this.profile.character);

    if (character === undefined) {
      this.add
        .text(this.centerX, height - 36, 'Tocá "★ Personajes" para elegir tu estrella ✨', {
          fontFamily: 'Trebuchet MS, sans-serif',
          fontSize: '20px',
          color: Palette.textLight,
          align: 'center',
          wordWrap: { width: width * 0.9 },
        })
        .setOrigin(0.5);
      return;
    }

    const avatarHeight = Math.min(180, height * 0.22);
    const baseY = height - avatarHeight / 2 - 36;

    const avatar = this.add.image(0, 0, character.texture).setOrigin(0.5);
    avatar.setScale(avatarHeight / avatar.height);

    const content: Phaser.GameObjects.GameObject[] = [avatar];
    const accessory = findShopItem(this.profile.accessory);
    if (accessory !== undefined) {
      const accessoryText = this.add
        .text(0, -avatarHeight * 0.42, accessory.emoji, { fontSize: `${Math.round(avatarHeight * 0.32)}px` })
        .setOrigin(0.5);
      content.push(accessoryText);
    }

    this.addCharacterDance(content, { x: this.centerX, y: baseY, size: avatarHeight });

    this.add
      .text(this.centerX, height - 20, character.name, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '24px',
        color: Palette.textLight,
      })
      .setOrigin(0.5);
  }

  private renderMinigameCards(minigames: MinigameDefinition[], gridTop: number): void {
    if (minigames.length === 0) {
      this.add
        .text(this.centerX, this.centerY, 'Pronto vas a tener minijuegos acá!', {
          fontFamily: 'Trebuchet MS, sans-serif',
          fontSize: '28px',
          color: Palette.textLight,
        })
        .setOrigin(0.5);
      return;
    }

    const { width, height } = this.scale;
    const footerReserved = Math.min(180, height * 0.22) + 70;
    const gridBottom = height - footerReserved;
    const gridAreaHeight = Math.max(160, gridBottom - gridTop);

    const gap = Math.max(16, width * 0.025);
    const idealColumnWidth = 250;
    let columns = Math.max(1, Math.floor((width * 0.92 + gap) / (idealColumnWidth + gap)));
    columns = Math.min(columns, minigames.length, 5);
    if (this.isPortrait) {
      columns = Math.min(columns, 2);
    }
    const rows = Math.ceil(minigames.length / columns);

    const cardWidth = Math.min(MAX_CARD_WIDTH, (width * 0.92 - (columns - 1) * gap) / columns);
    const cardHeight = Math.min(cardWidth * 0.78, (gridAreaHeight - (rows - 1) * gap) / rows);

    const totalHeight = rows * cardHeight + (rows - 1) * gap;
    const startY = gridTop + (gridAreaHeight - totalHeight) / 2 + cardHeight / 2;

    minigames.forEach((minigame, index) => {
      const column = index % columns;
      const row = Math.floor(index / columns);
      const cardsInRow = Math.min(columns, minigames.length - row * columns);
      const rowWidth = cardsInRow * cardWidth + (cardsInRow - 1) * gap;
      const startX = this.centerX - rowWidth / 2 + cardWidth / 2;

      const x = startX + column * (cardWidth + gap);
      const y = startY + row * (cardHeight + gap);
      this.createCard(minigame, x, y, cardWidth, cardHeight, index * 70);
    });
  }

  private createCard(
    minigame: MinigameDefinition,
    x: number,
    y: number,
    cardWidth: number,
    cardHeight: number,
    entranceDelay: number,
  ): void {
    const container = this.add.container(x, y);

    const background = this.add.graphics();
    background.fillStyle(minigame.color, 1);
    background.fillRoundedRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 22);

    const iconSize = Math.round(cardHeight * 0.34);
    const icon = this.add
      .text(0, -cardHeight * 0.14, minigame.icon, { fontSize: `${iconSize}px` })
      .setOrigin(0.5);

    const titleSize = Math.round(Math.min(26, cardWidth * 0.11));
    const title = this.add
      .text(0, cardHeight * 0.26, minigame.title, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: `${titleSize}px`,
        color: Palette.textLight,
        align: 'center',
        wordWrap: { width: cardWidth - 24 },
      })
      .setOrigin(0.5);

    container.add([background, icon, title]);
    container.setSize(cardWidth, cardHeight);
    container.setInteractive({ useHandCursor: true });

    container.setScale(0.7).setAlpha(0);
    this.tweens.add({
      targets: container,
      scale: 1,
      alpha: 1,
      duration: 300,
      delay: entranceDelay,
      ease: 'Back.easeOut',
    });

    container.on(Phaser.Input.Events.POINTER_OVER, () => container.setScale(1.05));
    container.on(Phaser.Input.Events.POINTER_OUT, () => container.setScale(1));
    container.on(Phaser.Input.Events.POINTER_UP, () => {
      this.audio.playClick();
      this.scene.start(minigame.key);
    });
  }
}
